// src/oura/components/newscan/brandReview.ts
//
// Brand mode (Visibility Set v4.5, SPEC-v45 §2, §7, §8) as the Review and
// Start steps read it: which draft is a brand set, the plain names of its
// groups and markets, and the one-line facts the health line and Start show.
// Pure functions, no React.
//
// The question row's `check` is still typed local-only (the staged flip in
// scanDraftTypes.ts), so brand checks are read through `checkOf`, which keeps
// compiling before and after the flip.

import type {
    AnyScanDraftJobStep,
    BrandQuestionCheck,
    ScanDraft,
    ScanDraftBusiness,
    ScanDraftQuestion,
    ScanQuestionCheck,
} from "@service/aeo/scanDraftTypes";

/* ---- what a row and a draft are ---- */

const BRAND_CHECKS: ReadonlyArray<ScanQuestionCheck> = ["pick", "source", "supplier"];

/** The row's check, both modes. */
export function checkOf(q: ScanDraftQuestion): ScanQuestionCheck {
    return q.check as ScanQuestionCheck;
}

export function isBrandCheck(c: ScanQuestionCheck): c is BrandQuestionCheck {
    return BRAND_CHECKS.includes(c);
}

/** The 50 were written in brand mode: a stored Google pull, or a row with a brand check. */
export function isBrandSet(d: ScanDraft): boolean {
    return Boolean(d.demand) || d.questions.some((q) => isBrandCheck(checkOf(q)));
}

/** The draft is being written in brand mode (the questions may not exist yet). */
export function isBrandBuild(d: ScanDraft): boolean {
    return d.business.mode === "brand" || (d.job?.step as AnyScanDraftJobStep | null | undefined) === "pulling_demand" || isBrandSet(d);
}

/* ---- groups ---- */

export type BrandGroupId = "pick_problem" | "pick_product" | "source" | "supplier";

export const BRAND_GROUPS: ReadonlyArray<{ id: BrandGroupId; title: string; short: string; intro: string }> = [
    {
        id: "pick_problem",
        title: "Recommend you · problems",
        short: "Recommend you",
        intro: "Buyers describe what they want, without the product word.",
    },
    { id: "pick_product", title: "Recommend you · products", short: "Recommend you", intro: "Buyers name the product." },
    { id: "source", title: "Cite your site", short: "Cite your site", intro: "Questions that explain the topic. Counted when AI cites your site." },
    { id: "supplier", title: "Business buyers", short: "Business buyers", intro: "A business looking for a supplier." },
];

/** The row's group: its recipe group when it is a brand one, else read from its check. */
export function brandGroupOf(q: ScanDraftQuestion): BrandGroupId {
    if (BRAND_GROUPS.some((g) => g.id === q.group)) return q.group as BrandGroupId;
    const c = checkOf(q);
    return c === "source" ? "source" : c === "supplier" ? "supplier" : "pick_problem";
}

/* ---- markets ---- */

const MARKET_NAME: Record<string, string> = {
    us: "the US",
    sg: "Singapore",
    my: "Malaysia",
    au: "Australia",
    uk: "the UK",
    global: "any market",
};

export interface MarketName {
    code: string;
    /** "US", "SG", "Any". */
    short: string;
    /** "the US", "Singapore", "any market": as written mid-sentence. */
    full: string;
}

/** A market code in plain words, the business's own label first. */
export function marketName(code: string, b?: ScanDraftBusiness): MarketName {
    const c = (code || "global").trim().toLowerCase();
    if (c === "global") return { code: c, short: "Any", full: MARKET_NAME.global };
    const own = b?.markets?.find((m) => m.code === c)?.label;
    return { code: c, short: c.toUpperCase(), full: own || MARKET_NAME[c] || c.toUpperCase() };
}

/** The market a brand row is asked for (its `market`, else its `fill`, which is the same code). */
export function marketOf(q: ScanDraftQuestion): string {
    return (q.market || q.fill || "global").trim().toLowerCase();
}

/** Capitalised for the start of a line or a title: "Any market", "The US". */
export function capital(s: string): string {
    return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** "the US, Singapore and Malaysia". */
export function joinAnd(items: string[]): string {
    if (items.length <= 1) return items[0] ?? "";
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** Market codes in the business's order, then any other code the rows or the pull use, `global` last. */
export function marketOrder(d: ScanDraft): string[] {
    const out: string[] = [];
    const add = (c: string | undefined) => {
        const code = (c ?? "").trim().toLowerCase();
        if (code && code !== "global" && !out.includes(code)) out.push(code);
    };
    d.business.markets?.forEach((m) => add(m.code));
    d.demand?.markets.forEach(add);
    if (isBrandSet(d)) d.questions.forEach((q) => add(marketOf(q)));
    return out;
}

/** The markets pulled, in plain words and in order: "the US, Singapore and Malaysia". */
export function pulledMarkets(d: ScanDraft): string {
    const codes = d.demand?.markets.length ? d.demand.markets : (d.business.markets ?? []).map((m) => m.code);
    return joinAnd(codes.filter((c) => c !== "global").map((c) => marketName(c, d.business).full));
}

/* ---- the one-line facts ---- */

function brandCounts(d: ScanDraft): { pick: number; source: number; supplier: number } {
    const counted = d.counts.check;
    const rows = d.questions.map(checkOf);
    const count = (c: BrandQuestionCheck) => counted[c] ?? rows.filter((r) => r === c).length;
    return { pick: count("pick"), source: count("source"), supplier: count("supplier") };
}

/** "28 recommend you · 14 cite your site · 8 business buyers" (shoppers only: no business buyers part). */
export function brandShape(d: ScanDraft): string {
    const { pick, source, supplier } = brandCounts(d);
    return [`${pick} recommend you`, `${source} cite your site`, supplier > 0 && `${supplier} business buyers`].filter(Boolean).join(" · ");
}

/** The recipe's shape: 28 / 14 / 8, or 36 / 14 / 0 for a shopper-only store. */
export function brandShapeOk(d: ScanDraft): boolean {
    const { pick, source, supplier } = brandCounts(d);
    return source === 14 && ((pick === 28 && supplier === 8) || (pick === 36 && supplier === 0));
}

/** Rows per market, in market order, `global` last. */
export function marketCounts(d: ScanDraft): Array<{ market: MarketName; n: number }> {
    const per = new Map<string, number>();
    d.questions.forEach((q) => {
        const c = marketOf(q);
        per.set(c, (per.get(c) ?? 0) + 1);
    });
    const order = [...marketOrder(d).filter((c) => per.has(c)), ...(per.has("global") ? ["global"] : [])];
    return order.map((c) => ({ market: marketName(c, d.business), n: per.get(c) ?? 0 }));
}

/** "The US 22 · Singapore 8 · Malaysia 2 · any market 18": it opens a line, so the first word is capitalised. */
export function marketLine(d: ScanDraft): string {
    return capital(
        marketCounts(d)
            .map(({ market, n }) => `${market.full} ${n}`)
            .join(" · "),
    );
}

/**
 * The markets the ChatGPT app capture can answer for: it runs from Malaysia, so
 * Malaysia and any market. SPEC-v45 §12.1.
 */
const CAPTURE_MARKETS: ReadonlyArray<string> = ["my", "global"];

/**
 * Markets of a brand set's rows that the capture cannot ask from their own
 * market yet, in market order, in plain words. Empty when the set can start.
 */
export function marketsNotCaptured(d: ScanDraft): string[] {
    if (!isBrandSet(d)) return [];
    const used = new Set(d.questions.map(marketOf));
    return marketOrder(d)
        .filter((c) => used.has(c) && !CAPTURE_MARKETS.includes(c))
        .map((c) => marketName(c, d.business).full);
}

/** The home market (last in the list) asks the fewest of the named markets. One market: always true. */
export function homeHoldsFewest(d: ScanDraft): boolean {
    const home = d.business.markets?.[d.business.markets.length - 1]?.code;
    const named = marketCounts(d).filter(({ market }) => market.code !== "global");
    const homeRow = named.find(({ market }) => market.code === home);
    if (!home || !homeRow || named.length < 2) return true;
    return named.every(({ market, n }) => market.code === home || n > homeRow.n);
}

/** How many rows a Google search supports. Null when no row says (an older draft): unknown, not 0. */
export function traceCounts(d: ScanDraft): { traced: number; total: number } | null {
    const told = d.questions.filter((q) => q.trace !== undefined);
    if (told.length === 0) return null;
    return { traced: told.filter((q) => q.trace).length, total: d.questions.length };
}

/** Question numbers named by the open decisions of a type. */
export function openSlots(d: ScanDraft, type: ScanDraft["decisions"][number]["type"]): number[] {
    const out = new Set<number>();
    d.decisions.filter((x) => x.state === "open" && x.type === type).forEach((x) => x.slots.forEach((n) => out.add(n)));
    return Array.from(out);
}

/** "15 Oct", from the pull's ISO time. Null when it does not parse. */
export function shortDate(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return t.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
